// @story HIR-TC1
// HR types in a panellist's paper mark sheet (server: services/
// panelOnBehalf.service.js). The panellist is picked by their account —
// shown with their email wherever two share a name — and the marks are
// recorded as judged by them and entered by whoever is signed in. If they
// already have a mark here the server says so, and HR must confirm before
// the paper marks replace it (the earlier mark stays in the history).
import React, { useState } from "react";
import api from "../services/api";
import { Button } from "../ds";
import RubricMark, { blankRow, markTotal } from "./RubricMark";

const muted = { color: "var(--text-muted)", fontSize: "var(--fs-caption)" };
const field = { width: "100%", border: "1px solid var(--border, #ddd)", borderRadius: 6, padding: "6px 8px", font: "inherit", fontSize: "var(--fs-body-sm)" };

export default function PaperMarksForm({ jobId, stageId, resumeId, criteria = [], people = [], onSaved }) {
  const [who, setWho] = useState("");
  const blank = () => criteria.map((c) => ({ ...blankRow(c), score: "" }));
  const [marks, setMarks] = useState(blank);
  const [overall, setOverall] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [duplicate, setDuplicate] = useState("");

  const save = async (confirmDuplicate = false) => {
    if (!who) { setErr("Choose whose marks these are."); return; }
    if (criteria.length && !markTotal(marks).complete) { setErr("Give every criterion a level from 1 to 4."); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/human-interview/${jobId}/${stageId}/assessment/${resumeId}/on-behalf`, {
        panellistUserId: who,
        ...(criteria.length
          ? { scores: marks.map((m) => ({ label: m.label, score: m.score === "" ? (m.scale === "levels" ? null : 0) : Number(m.score) })) }
          : { overall: Number(overall) }),
        notes,
        ...(confirmDuplicate ? { confirmDuplicate: true } : {}),
      });
      setDuplicate("");
      setWho(""); setNotes(""); setOverall("");
      setMarks(blank());
      if (onSaved) onSaved();
    } catch (e) {
      if (e.response?.data?.code === "duplicate_mark") setDuplicate(e.response.data.message);
      else setErr(e.response?.data?.message || "Could not save those marks just now.");
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={muted}>For a panellist who marked on paper. Recorded as judged by them and entered by you.</div>
      <select aria-label="Panellist" value={who} onChange={(e) => { setWho(e.target.value); setDuplicate(""); }} style={field}>
        <option value="">Whose marks are these?</option>
        {people.map((p) => <option key={p.userId} value={p.userId}>{p.label || `${p.name} (${p.email})`}</option>)}
      </select>
      {criteria.length ? marks.map((m, i) => (
        <div key={m.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: "var(--fs-body-sm)" }}>
          <label htmlFor={`paper-mark-${i}`}>{m.label}</label>
          <RubricMark id={`paper-mark-${i}`} row={m} onChange={(v) => setMarks((all) => all.map((x, j) => (j === i ? { ...x, score: v } : x)))} />
        </div>
      )) : (
        <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: "var(--fs-body-sm)" }}>
          <span>Overall mark <span style={muted}>/ 100</span></span>
          <input aria-label="Overall mark" type="number" min={0} max={100} value={overall} style={{ ...field, width: 90 }} onChange={(e) => setOverall(e.target.value)} />
        </label>
      )}
      <textarea aria-label="Notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes from the mark sheet (optional)" style={field} />
      {duplicate ? (
        <div role="alert" style={{ display: "grid", gap: 6, fontSize: "var(--fs-body-sm)", color: "var(--status-warning-text)" }}>
          <span>{duplicate}</span>
          <div style={{ display: "flex", gap: 6 }}>
            <Button size="sm" onClick={() => save(true)} disabled={busy}>Replace with the paper marks</Button>
            <Button size="sm" variant="ghost" onClick={() => setDuplicate("")} disabled={busy}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button variant="secondary" onClick={() => save(false)} disabled={busy || !who}>Save paper marks</Button>
      )}
      {err && <span role="alert" style={{ ...muted, color: "var(--status-danger-text)" }}>{err}</span>}
    </div>
  );
}
